import { useState } from "react"
import {
  Phone,
  Mail,
  Calendar,
  StickyNote,
  MessageSquare,
  CheckCircle2,
  ArrowRightLeft,
  Edit3,
  Zap,
  Plus,
  Clock,
} from "lucide-react"
import { useEntityTimeline, useCreateActivity } from "@/hooks/use-activities"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Skeleton } from "@/components/ui/skeleton"
import { formatDate, formatTime } from "@/lib/utils"
import { toast } from "sonner"
import type { ActivityType, Activity } from "@/lib/types"

const ACTIVITY_TYPE_CONFIG: Record<
  string,
  { icon: React.ElementType; label: string; color: string }
> = {
  phone: { icon: Phone, label: "Telefon", color: "#3B82F6" },
  email: { icon: Mail, label: "Email", color: "#8B5CF6" },
  meeting: { icon: Calendar, label: "Spotkanie", color: "#F59E0B" },
  note: { icon: StickyNote, label: "Notatka", color: "#6B7280" },
  sms: { icon: MessageSquare, label: "SMS", color: "#14B8A6" },
  task_completed: { icon: CheckCircle2, label: "Zadanie", color: "#22C55E" },
  phase_changed: { icon: ArrowRightLeft, label: "Zmiana etapu", color: "#EC4899" },
  field_changed: { icon: Edit3, label: "Zmiana pola", color: "#F97316" },
  system: { icon: Zap, label: "System", color: "#6366F1" },
}

const MANUAL_TYPES: { value: ActivityType; label: string }[] = [
  { value: "phone", label: "Telefon" },
  { value: "email", label: "Email" },
  { value: "meeting", label: "Spotkanie" },
  { value: "note", label: "Notatka" },
  { value: "sms", label: "SMS" },
]

function ActivityIcon({ type }: { type: string }) {
  const config = ACTIVITY_TYPE_CONFIG[type] || ACTIVITY_TYPE_CONFIG.system
  const Icon = config.icon
  return (
    <div
      className="flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0"
      style={{ backgroundColor: config.color + "18", color: config.color }}
    >
      <Icon size={14} />
    </div>
  )
}

function ActivityItem({ activity }: { activity: Activity }) {
  const config = ACTIVITY_TYPE_CONFIG[activity.activity_type] || ACTIVITY_TYPE_CONFIG.system
  const metadata = activity.metadata as Record<string, string>

  return (
    <div className="flex gap-3 py-3">
      <ActivityIcon type={activity.activity_type} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium px-1.5 py-0.5 rounded" style={{ backgroundColor: config.color + "12", color: config.color }}>
            {config.label}
          </span>
          {activity.is_auto && (
            <span className="text-[10px] px-1 py-0.5 rounded bg-muted text-muted-foreground">
              Auto
            </span>
          )}
        </div>
        {activity.subject && (
          <p className="text-sm font-medium mt-1 text-foreground">
            {activity.subject}
          </p>
        )}
        {activity.description && (
          <p className="text-sm mt-0.5 line-clamp-2 text-muted-foreground">
            {activity.description}
          </p>
        )}
        {/* Phase change metadata */}
        {activity.activity_type === "phase_changed" && metadata.from_phase && (
          <p className="text-sm mt-0.5 text-muted-foreground">
            {metadata.from_phase} → <strong>{metadata.to_phase}</strong>
          </p>
        )}
        <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
          <span>{formatDate(activity.activity_date)}, {formatTime(activity.activity_date)}</span>
          {activity.duration_minutes != null && activity.duration_minutes > 0 && (
            <span className="flex items-center gap-1">
              <Clock size={10} /> {activity.duration_minutes} min
            </span>
          )}
          {activity.user && (
            <span>{activity.user.first_name} {activity.user.last_name}</span>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Quick Log Dialog ─────────────────────────────────────────────

function QuickLogDialog({
  open,
  onOpenChange,
  entityType,
  entityId,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  entityType: string
  entityId: string
}) {
  const [activityType, setActivityType] = useState<ActivityType>("note")
  const [subject, setSubject] = useState("")
  const [description, setDescription] = useState("")
  const [duration, setDuration] = useState("")
  const createMutation = useCreateActivity()

  const handleSave = async () => {
    try {
      await createMutation.mutateAsync({
        activity_type: activityType,
        subject: subject || null,
        description: description || null,
        duration_minutes: duration ? parseInt(duration) : null,
        entity_type: entityType,
        entity_id: entityId,
      })
      toast.success("Aktywność dodana")
      onOpenChange(false)
      setSubject("")
      setDescription("")
      setDuration("")
    } catch {
      toast.error("Nie udało się dodać aktywności")
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Dodaj aktywność</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-muted-foreground">Typ</label>
            <div className="flex flex-wrap gap-2">
              {MANUAL_TYPES.map((mt) => {
                const config = ACTIVITY_TYPE_CONFIG[mt.value]
                const Icon = config.icon
                const isSelected = activityType === mt.value
                return (
                  <button
                    key={mt.value}
                    onClick={() => setActivityType(mt.value)}
                    className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors border ${
                      isSelected ? "" : "border-input text-muted-foreground"
                    }`}
                    style={isSelected ? {
                      borderColor: config.color,
                      backgroundColor: config.color + "12",
                      color: config.color,
                    } : undefined}
                  >
                    <Icon size={12} />
                    {mt.label}
                  </button>
                )
              })}
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-muted-foreground">Temat</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm text-foreground outline-none"
              placeholder="np. Rozmowa z klientem"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-muted-foreground">Opis</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm text-foreground outline-none"
              rows={3}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-muted-foreground">Czas trwania (min)</label>
            <input
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              type="number"
              min="0"
              className="h-9 w-full max-w-[120px] rounded-md border border-input bg-transparent px-3 text-sm text-foreground outline-none"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Anuluj</Button>
          <Button onClick={handleSave} disabled={createMutation.isPending}>
            {createMutation.isPending ? "Zapisywanie..." : "Dodaj"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Main Timeline Component ──────────────────────────────────────

export function ActivityTimeline({
  entityType,
  entityId,
}: {
  entityType: string
  entityId: string
}) {
  const { data, isLoading } = useEntityTimeline(entityType, entityId)
  const [showQuickLog, setShowQuickLog] = useState(false)
  const [typeFilter, setTypeFilter] = useState<string>("")

  const activities = data?.data ?? []
  const filtered = typeFilter
    ? activities.filter((a) => a.activity_type === typeFilter)
    : activities

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="h-8 rounded-md border border-input bg-transparent px-2 text-xs text-foreground"
          >
            <option value="">Wszystkie typy</option>
            {Object.entries(ACTIVITY_TYPE_CONFIG).map(([key, cfg]) => (
              <option key={key} value={key}>{cfg.label}</option>
            ))}
          </select>
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowQuickLog(true)}>
          <Plus size={14} /> Dodaj aktywność
        </Button>
      </div>

      {/* Timeline */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-input py-8 text-muted-foreground">
          <p className="text-sm">Brak aktywności</p>
          <Button size="sm" variant="outline" onClick={() => setShowQuickLog(true)}>
            <Plus size={14} /> Dodaj aktywność
          </Button>
        </div>
      ) : (
        <div className="divide-y divide-border">
          {filtered.map((activity) => (
            <ActivityItem key={activity.id} activity={activity} />
          ))}
        </div>
      )}

      {/* Quick log dialog */}
      <QuickLogDialog
        open={showQuickLog}
        onOpenChange={setShowQuickLog}
        entityType={entityType}
        entityId={entityId}
      />
    </div>
  )
}
